import * as THREE from 'three';

export class GameScene {
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public renderer: THREE.WebGLRenderer;

  private canvas: HTMLCanvasElement;
  private sun!: THREE.Mesh;
  private clouds: THREE.Mesh[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // 创建场景
    this.scene = new THREE.Scene();

    // 创建相机（必须在setupSky之前，因为太阳着色器需要相机位置）
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      3000 // 增大远裁剪面以支持更大地图
    );
    this.camera.position.set(0, 25, -35); // 增大初始相机距离和高度
    this.camera.lookAt(0, 0, 0);

    // 设置天空系统（需要camera已存在）
    this.setupSky();

    // 改进的雾效
    this.scene.fog = new THREE.FogExp2(0xd0e7f9, 0.008);

    // 创建渲染器
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    // 设置灯光
    this.setupLights();

    // 创建默认地面和墙壁
    this.createGround(50);
    this.createWalls(50);

    // 添加环境装饰
    this.createEnvironmentProps(50);

    // 处理窗口大小调整
    window.addEventListener('resize', () => this.onWindowResize());
  }

  private setupSky(): void {
    // 创建天空盒（使用渐变着色器）
    const skyGeometry = new THREE.SphereGeometry(500, 32, 32);
    const skyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(0x0077ff) },
        bottomColor: { value: new THREE.Color(0xffffff) },
        offset: { value: 33 },
        exponent: { value: 0.6 }
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `,
      side: THREE.BackSide
    });

    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    this.scene.add(sky);

    // 创建太阳
    this.createSun();

    // 创建云朵
    this.createClouds();
  }

  private createSun(): void {
    // 太阳光晕
    const sunGlowGeometry = new THREE.SphereGeometry(8, 32, 32);
    const sunGlowMaterial = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(0xffff88) },
        viewVector: { value: this.camera.position }
      },
      vertexShader: `
        uniform vec3 viewVector;
        varying float intensity;
        void main() {
          vec3 vNormal = normalize(normalMatrix * normal);
          vec3 vNormel = normalize(normalMatrix * viewVector);
          intensity = pow(0.6 - dot(vNormal, vNormel), 2.0);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        varying float intensity;
        void main() {
          vec3 glow = glowColor * intensity;
          gl_FragColor = vec4(glow, 1.0);
        }
      `,
      side: THREE.FrontSide,
      blending: THREE.AdditiveBlending,
      transparent: true
    });

    const sunGlow = new THREE.Mesh(sunGlowGeometry, sunGlowMaterial);
    sunGlow.position.set(100, 80, 100);

    // 太阳核心
    const sunCoreGeometry = new THREE.SphereGeometry(6, 32, 32);
    const sunCoreMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffdd,
      transparent: true,
      opacity: 0.9
    });

    this.sun = new THREE.Mesh(sunCoreGeometry, sunCoreMaterial);
    this.sun.position.copy(sunGlow.position);

    this.scene.add(sunGlow);
    this.scene.add(this.sun);
  }

  private createClouds(): void {
    // 创建多个程序化云朵
    const cloudCount = 15;

    for (let i = 0; i < cloudCount; i++) {
      const cloud = this.createSingleCloud();

      // 随机位置
      const angle = Math.random() * Math.PI * 2;
      const distance = 100 + Math.random() * 150;
      cloud.position.set(
        Math.cos(angle) * distance,
        30 + Math.random() * 40,
        Math.sin(angle) * distance
      );

      // 随机旋转
      cloud.rotation.y = Math.random() * Math.PI * 2;

      // 随机缩放
      const scale = 0.5 + Math.random() * 1.5;
      cloud.scale.set(scale, scale * 0.6, scale);

      this.clouds.push(cloud);
      this.scene.add(cloud);
    }
  }

  private createSingleCloud(): THREE.Mesh {
    // 使用多个球体组合创建云朵
    const cloudGroup = new THREE.Group();
    const cloudMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.7,
      roughness: 1.0,
      metalness: 0.0
    });

    // 创建3-5个球体组成一朵云
    const sphereCount = 3 + Math.floor(Math.random() * 3);

    for (let i = 0; i < sphereCount; i++) {
      const size = 3 + Math.random() * 4;
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(size, 8, 8),
        cloudMaterial
      );

      sphere.position.set(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 3,
        (Math.random() - 0.5) * 10
      );

      cloudGroup.add(sphere);
    }

    // 将group转换为mesh以便于管理
    const cloudMesh = new THREE.Mesh();
    cloudMesh.add(cloudGroup);

    return cloudMesh;
  }

  private setupLights(): void {
    // 环境光（降低强度以增加对比度）
    const ambientLight = new THREE.AmbientLight(0xb3d9ff, 0.4);
    this.scene.add(ambientLight);

    // 主太阳光（平行光）
    const sunLight = new THREE.DirectionalLight(0xfffacd, 1.2);
    sunLight.position.set(100, 80, 100);
    sunLight.castShadow = true;

    // 优化阴影参数
    sunLight.shadow.camera.left = -60;
    sunLight.shadow.camera.right = 60;
    sunLight.shadow.camera.top = 60;
    sunLight.shadow.camera.bottom = -60;
    sunLight.shadow.camera.near = 0.1;
    sunLight.shadow.camera.far = 300;
    sunLight.shadow.mapSize.width = 2048;
    sunLight.shadow.mapSize.height = 2048;
    sunLight.shadow.bias = -0.0001;

    this.scene.add(sunLight);

    // 补光（半球光）
    const hemisphereLight = new THREE.HemisphereLight(
      0xffffff, // 天空颜色
      0x8b7355, // 地面颜色
      0.5
    );
    this.scene.add(hemisphereLight);

    // 添加一些点光源作为环境细节光
    this.addEnvironmentLights();
  }

  private addEnvironmentLights(): void {
    // 在战场周围添加微弱的点光源，增加氛围
    const lightPositions = [
      { x: 40, y: 5, z: 40, color: 0xffaa66, intensity: 0.3 },
      { x: -40, y: 5, z: 40, color: 0xffaa66, intensity: 0.3 },
      { x: 40, y: 5, z: -40, color: 0xffaa66, intensity: 0.3 },
      { x: -40, y: 5, z: -40, color: 0xffaa66, intensity: 0.3 }
    ];

    lightPositions.forEach(pos => {
      const pointLight = new THREE.PointLight(pos.color, pos.intensity, 30);
      pointLight.position.set(pos.x, pos.y, pos.z);
      this.scene.add(pointLight);
    });
  }

  update(deltaTime: number): void {
    // 云朵动画（缓慢飘动）
    this.clouds.forEach((cloud, index) => {
      const speed = 0.5 * deltaTime;
      cloud.position.x += Math.sin(index) * speed;
      cloud.position.z += Math.cos(index) * speed;

      // 边界检查，让云朵循环
      if (cloud.position.length() > 300) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 100 + Math.random() * 50;
        cloud.position.set(
          Math.cos(angle) * distance,
          cloud.position.y,
          Math.sin(angle) * distance
        );
      }
    });
  }

  createGround(size: number = 100, color: number = 0x8B7355): void {
    // 移除旧地面
    const oldGround = this.scene.getObjectByName('ground');
    const oldGrid = this.scene.getObjectByName('grid');
    const oldDetail = this.scene.getObjectByName('ground-detail');
    if (oldGround) this.scene.remove(oldGround);
    if (oldGrid) this.scene.remove(oldGrid);
    if (oldDetail) this.scene.remove(oldDetail);

    // 创建主地面（更高细分度）
    const groundGeometry = new THREE.PlaneGeometry(size * 2, size * 2, 100, 100);

    // 添加高度变化（微妙的地形起伏）
    const vertices = groundGeometry.attributes.position.array;
    for (let i = 0; i < vertices.length; i += 3) {
      const x = vertices[i];
      const z = vertices[i + 1];

      // 使用柏林噪声风格的变化
      vertices[i + 2] =
        Math.sin(x * 0.1) * Math.cos(z * 0.1) * 0.3 +
        Math.sin(x * 0.05) * Math.sin(z * 0.05) * 0.5 +
        (Math.random() - 0.5) * 0.2;
    }
    groundGeometry.computeVertexNormals();

    // 创建程序化纹理材质
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // 基础泥土色
    ctx.fillStyle = `rgb(${(color >> 16) & 255}, ${(color >> 8) & 255}, ${color & 255})`;
    ctx.fillRect(0, 0, 512, 512);

    // 添加噪声（泥土细节）
    for (let i = 0; i < 10000; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const brightness = Math.random() * 50 - 25;
      const r = Math.min(255, Math.max(0, ((color >> 16) & 255) + brightness));
      const g = Math.min(255, Math.max(0, ((color >> 8) & 255) + brightness));
      const b = Math.min(255, Math.max(0, (color & 255) + brightness));

      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(x, y, 2, 2);
    }

    // 添加裂纹效果
    ctx.strokeStyle = 'rgba(50, 40, 30, 0.3)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 30; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * 512, Math.random() * 512);
      for (let j = 0; j < 5; j++) {
        ctx.lineTo(
          Math.random() * 512,
          Math.random() * 512
        );
      }
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(8, 8);

    const groundMaterial = new THREE.MeshStandardMaterial({
      map: texture,
      color: 0xffffff,
      roughness: 0.95,
      metalness: 0.05,
      bumpScale: 0.5
    });

    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.name = 'ground';
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    // 添加地面细节（随机的小石块和草丛）
    this.addGroundDetails(size);

    // 改进的网格线（更淡、更自然）
    const gridHelper = new THREE.GridHelper(size * 2, 50, 0x666666, 0x999999);
    gridHelper.name = 'grid';
    gridHelper.material.opacity = 0.15;
    gridHelper.material.transparent = true;
    this.scene.add(gridHelper);
  }

  private addGroundDetails(size: number): void {
    const detailGroup = new THREE.Group();
    detailGroup.name = 'ground-detail';

    // 添加随机石块
    for (let i = 0; i < 40; i++) {
      const rockSize = 0.3 + Math.random() * 0.8;
      const rockGeometry = new THREE.DodecahedronGeometry(rockSize, 0);
      const rockMaterial = new THREE.MeshStandardMaterial({
        color: 0x555555,
        roughness: 0.9,
        metalness: 0.1
      });

      const rock = new THREE.Mesh(rockGeometry, rockMaterial);
      rock.position.set(
        (Math.random() - 0.5) * size * 1.8,
        rockSize * 0.3,
        (Math.random() - 0.5) * size * 1.8
      );
      rock.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      rock.castShadow = true;
      rock.receiveShadow = true;

      detailGroup.add(rock);
    }

    // 添加干草/枯草簇
    for (let i = 0; i < 60; i++) {
      const grassCluster = this.createGrassCluster();
      grassCluster.position.set(
        (Math.random() - 0.5) * size * 1.8,
        0,
        (Math.random() - 0.5) * size * 1.8
      );
      grassCluster.rotation.y = Math.random() * Math.PI * 2;

      detailGroup.add(grassCluster);
    }

    this.scene.add(detailGroup);
  }

  private createGrassCluster(): THREE.Group {
    const cluster = new THREE.Group();
    const grassMaterial = new THREE.MeshStandardMaterial({
      color: 0x9b8f5f,
      roughness: 0.9,
      metalness: 0.0,
      side: THREE.DoubleSide
    });

    // 创建3-7根草叶
    const bladeCount = 3 + Math.floor(Math.random() * 5);

    for (let i = 0; i < bladeCount; i++) {
      const height = 0.8 + Math.random() * 1.2;
      const width = 0.1;

      const geometry = new THREE.PlaneGeometry(width, height);
      const blade = new THREE.Mesh(geometry, grassMaterial);

      blade.position.set(
        (Math.random() - 0.5) * 0.3,
        height / 2,
        (Math.random() - 0.5) * 0.3
      );

      // 轻微倾斜
      blade.rotation.z = (Math.random() - 0.5) * 0.3;
      blade.rotation.y = (i / bladeCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;

      cluster.add(blade);
    }

    return cluster;
  }

  private createEnvironmentProps(size: number): void {
    const propsGroup = new THREE.Group();
    propsGroup.name = 'environment-props';

    // 添加绿草地区域
    this.addGrassPatches(propsGroup, size);

    // 添加树木
    this.addTrees(propsGroup, size);

    // 添加房屋建筑
    this.addBuildings(propsGroup, size);

    // 添加河流
    this.addRiver(propsGroup, size);

    // 添加大型岩石
    this.addLargeRocks(propsGroup, size);

    // 添加废墟结构
    this.addRuins(propsGroup, size);

    // 添加战场残骸
    this.addBattleDebris(propsGroup, size);

    // 添加其他装饰
    this.addDecorations(propsGroup, size);

    this.scene.add(propsGroup);
  }

  private addLargeRocks(group: THREE.Group, size: number): void {
    // 创建8-12个大型岩石群
    const rockCount = 8 + Math.floor(Math.random() * 5);

    for (let i = 0; i < rockCount; i++) {
      const rockCluster = new THREE.Group();

      // 每个岩石群包含2-4块岩石
      const clusterSize = 2 + Math.floor(Math.random() * 3);

      for (let j = 0; j < clusterSize; j++) {
        const rockSize = 2 + Math.random() * 3;
        const rockGeometry = new THREE.DodecahedronGeometry(rockSize, 1);

        // 变形以获得更自然的岩石形状
        const positions = rockGeometry.attributes.position;
        for (let k = 0; k < positions.count; k++) {
          const x = positions.getX(k);
          const y = positions.getY(k);
          const z = positions.getZ(k);

          positions.setXYZ(
            k,
            x * (0.8 + Math.random() * 0.4),
            y * (0.8 + Math.random() * 0.4),
            z * (0.8 + Math.random() * 0.4)
          );
        }
        rockGeometry.computeVertexNormals();

        const rockMaterial = new THREE.MeshStandardMaterial({
          color: 0x6b6b6b,
          roughness: 0.9,
          metalness: 0.1,
          flatShading: true
        });

        const rock = new THREE.Mesh(rockGeometry, rockMaterial);
        rock.position.set(
          (Math.random() - 0.5) * 3,
          rockSize * 0.4,
          (Math.random() - 0.5) * 3
        );
        rock.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );
        rock.castShadow = true;
        rock.receiveShadow = true;

        rockCluster.add(rock);
      }

      // 放置岩石群（避开中心区域）
      const angle = (i / rockCount) * Math.PI * 2;
      const distance = size * 0.5 + Math.random() * size * 0.3;
      rockCluster.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );

      group.add(rockCluster);
    }
  }

  private addRuins(group: THREE.Group, size: number): void {
    // 添加5-8个废墟结构
    const ruinCount = 5 + Math.floor(Math.random() * 4);

    const concreteMaterial = new THREE.MeshStandardMaterial({
      color: 0x888888,
      roughness: 0.8,
      metalness: 0.2
    });

    for (let i = 0; i < ruinCount; i++) {
      const ruin = new THREE.Group();

      // 随机创建不同类型的废墟
      const ruinType = Math.floor(Math.random() * 3);

      if (ruinType === 0) {
        // 倒塌的墙壁
        const wallGeometry = new THREE.BoxGeometry(4, 3, 0.5);
        const wall = new THREE.Mesh(wallGeometry, concreteMaterial);
        wall.rotation.x = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
        wall.rotation.z = (Math.random() - 0.5) * 0.5;
        wall.position.y = 1;
        wall.castShadow = true;
        wall.receiveShadow = true;
        ruin.add(wall);
      } else if (ruinType === 1) {
        // 破碎的柱子
        const pillarHeight = 3 + Math.random() * 2;
        const pillarGeometry = new THREE.CylinderGeometry(0.4, 0.5, pillarHeight, 8);
        const pillar = new THREE.Mesh(pillarGeometry, concreteMaterial);
        pillar.rotation.z = Math.PI / 6 + (Math.random() - 0.5) * 0.3;
        pillar.position.y = pillarHeight * 0.3;
        pillar.castShadow = true;
        pillar.receiveShadow = true;
        ruin.add(pillar);

        // 添加破碎的顶部
        const capGeometry = new THREE.BoxGeometry(1, 0.5, 1);
        const cap = new THREE.Mesh(capGeometry, concreteMaterial);
        cap.position.y = pillarHeight * 0.5;
        cap.rotation.y = Math.random() * Math.PI;
        cap.castShadow = true;
        cap.receiveShadow = true;
        ruin.add(cap);
      } else {
        // 碎石堆
        for (let j = 0; j < 5 + Math.random() * 5; j++) {
          const rubbleSize = 0.5 + Math.random() * 1;
          const rubbleGeometry = new THREE.BoxGeometry(
            rubbleSize,
            rubbleSize * 0.8,
            rubbleSize * 0.6
          );
          const rubble = new THREE.Mesh(rubbleGeometry, concreteMaterial);
          rubble.position.set(
            (Math.random() - 0.5) * 2,
            rubbleSize * 0.5,
            (Math.random() - 0.5) * 2
          );
          rubble.rotation.set(
            Math.random() * Math.PI,
            Math.random() * Math.PI,
            Math.random() * Math.PI
          );
          rubble.castShadow = true;
          rubble.receiveShadow = true;
          ruin.add(rubble);
        }
      }

      // 放置废墟
      const angle = (i / ruinCount) * Math.PI * 2 + Math.random() * 0.5;
      const distance = size * 0.4 + Math.random() * size * 0.4;
      ruin.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );
      ruin.rotation.y = Math.random() * Math.PI * 2;

      group.add(ruin);
    }
  }

  private addBattleDebris(group: THREE.Group, size: number): void {
    // 添加战场残骸（烧毁的载具残骸）
    const debrisCount = 3 + Math.floor(Math.random() * 3);

    const burntMetalMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a2a,
      roughness: 0.9,
      metalness: 0.6
    });

    for (let i = 0; i < debrisCount; i++) {
      const debris = new THREE.Group();

      // 车体残骸
      const hullGeometry = new THREE.BoxGeometry(3, 1, 4);
      const hull = new THREE.Mesh(hullGeometry, burntMetalMaterial);
      hull.position.y = 0.5;
      hull.rotation.y = (Math.random() - 0.5) * 0.5;
      hull.rotation.z = (Math.random() - 0.5) * 0.3;
      hull.castShadow = true;
      hull.receiveShadow = true;
      debris.add(hull);

      // 炮塔残骸
      if (Math.random() > 0.5) {
        const turretGeometry = new THREE.CylinderGeometry(1, 1.2, 0.8, 8);
        const turret = new THREE.Mesh(turretGeometry, burntMetalMaterial);
        turret.position.set(
          (Math.random() - 0.5) * 2,
          0.5,
          (Math.random() - 0.5) * 2
        );
        turret.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );
        turret.castShadow = true;
        turret.receiveShadow = true;
        debris.add(turret);
      }

      // 添加一些金属碎片
      for (let j = 0; j < 3; j++) {
        const fragmentGeometry = new THREE.BoxGeometry(
          0.3 + Math.random() * 0.5,
          0.1,
          0.5 + Math.random() * 0.5
        );
        const fragment = new THREE.Mesh(fragmentGeometry, burntMetalMaterial);
        fragment.position.set(
          (Math.random() - 0.5) * 4,
          0.1,
          (Math.random() - 0.5) * 4
        );
        fragment.rotation.y = Math.random() * Math.PI * 2;
        fragment.castShadow = true;
        fragment.receiveShadow = true;
        debris.add(fragment);
      }

      // 放置残骸
      const angle = (i / debrisCount) * Math.PI * 2 + Math.random();
      const distance = size * 0.3 + Math.random() * size * 0.5;
      debris.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );
      debris.rotation.y = Math.random() * Math.PI * 2;

      group.add(debris);
    }
  }

  private addGrassPatches(group: THREE.Group, size: number): void {
    // 添加8-15个绿草地区域
    const patchCount = 8 + Math.floor(Math.random() * 8);

    for (let i = 0; i < patchCount; i++) {
      const patchSize = 6 + Math.random() * 8;
      const grassPatch = new THREE.Group();

      // 创建草地基础（平面）
      const patchGeometry = new THREE.CircleGeometry(patchSize, 32);
      const patchMaterial = new THREE.MeshStandardMaterial({
        color: 0x2d5016, // 深绿色
        roughness: 0.9,
        metalness: 0.0
      });

      const patch = new THREE.Mesh(patchGeometry, patchMaterial);
      patch.rotation.x = -Math.PI / 2;
      patch.position.y = 0.05; // 略高于地面
      patch.receiveShadow = true;
      grassPatch.add(patch);

      // 在草地上添加3D草丛
      const grassTufts = 15 + Math.floor(Math.random() * 20);
      for (let j = 0; j < grassTufts; j++) {
        const tuft = this.createGrassTuft();
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * patchSize * 0.9;
        tuft.position.set(
          Math.cos(angle) * distance,
          0,
          Math.sin(angle) * distance
        );
        grassPatch.add(tuft);
      }

      // 放置草地
      const angle = (i / patchCount) * Math.PI * 2 + Math.random() * 0.8;
      const distance = size * 0.2 + Math.random() * size * 0.6;
      grassPatch.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );

      group.add(grassPatch);
    }
  }

  private createGrassTuft(): THREE.Group {
    const tuft = new THREE.Group();
    const grassMaterial = new THREE.MeshStandardMaterial({
      color: 0x3a7d1f, // 鲜绿色
      roughness: 0.95,
      metalness: 0.0,
      side: THREE.DoubleSide
    });

    // 创建5-8片草叶
    const bladeCount = 5 + Math.floor(Math.random() * 4);
    for (let i = 0; i < bladeCount; i++) {
      const height = 1.2 + Math.random() * 1.5;
      const blade = new THREE.Mesh(
        new THREE.PlaneGeometry(0.15, height),
        grassMaterial
      );

      blade.position.y = height / 2;
      blade.rotation.y = (i / bladeCount) * Math.PI * 2;
      blade.rotation.z = (Math.random() - 0.5) * 0.4;

      tuft.add(blade);
    }

    return tuft;
  }

  private addTrees(group: THREE.Group, size: number): void {
    // 添加12-20棵树
    const treeCount = 12 + Math.floor(Math.random() * 9);

    for (let i = 0; i < treeCount; i++) {
      const treeType = Math.random();
      let tree: THREE.Group;

      if (treeType < 0.4) {
        tree = this.createPineTree();
      } else if (treeType < 0.7) {
        tree = this.createOakTree();
      } else {
        tree = this.createPalmTree();
      }

      // 放置树木（避免太靠近中心）
      const angle = (i / treeCount) * Math.PI * 2 + Math.random() * 0.5;
      const distance = size * 0.5 + Math.random() * size * 0.4;
      tree.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );
      tree.rotation.y = Math.random() * Math.PI * 2;

      group.add(tree);
    }
  }

  private createPineTree(): THREE.Group {
    const tree = new THREE.Group();

    // 树干
    const trunkGeometry = new THREE.CylinderGeometry(0.3, 0.4, 6, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a3728,
      roughness: 0.9,
      metalness: 0.0
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 3;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    tree.add(trunk);

    // 松树冠（3层锥形）
    const foliageMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a5c1a,
      roughness: 0.8,
      metalness: 0.0
    });

    for (let i = 0; i < 3; i++) {
      const radius = 2.5 - i * 0.6;
      const height = 3;
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(radius, height, 8),
        foliageMaterial
      );
      cone.position.y = 5.5 + i * 2;
      cone.castShadow = true;
      cone.receiveShadow = true;
      tree.add(cone);
    }

    return tree;
  }

  private createOakTree(): THREE.Group {
    const tree = new THREE.Group();

    // 树干
    const trunkGeometry = new THREE.CylinderGeometry(0.4, 0.6, 5, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x6b4423,
      roughness: 0.95,
      metalness: 0.0
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 2.5;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    tree.add(trunk);

    // 树冠（多个球体组成）
    const foliageMaterial = new THREE.MeshStandardMaterial({
      color: 0x2d7a2d,
      roughness: 0.85,
      metalness: 0.0
    });

    const foliagePositions = [
      { x: 0, y: 5.5, z: 0, r: 2.5 },
      { x: 1.2, y: 5, z: 0.8, r: 1.8 },
      { x: -1, y: 5.2, z: -0.9, r: 1.6 },
      { x: 0.5, y: 6.5, z: -1.2, r: 1.5 },
      { x: -0.8, y: 6, z: 1, r: 1.4 }
    ];

    foliagePositions.forEach(pos => {
      const foliage = new THREE.Mesh(
        new THREE.SphereGeometry(pos.r, 8, 8),
        foliageMaterial
      );
      foliage.position.set(pos.x, pos.y, pos.z);
      foliage.castShadow = true;
      foliage.receiveShadow = true;
      tree.add(foliage);
    });

    return tree;
  }

  private createPalmTree(): THREE.Group {
    const tree = new THREE.Group();

    // 棕榈树干（略微弯曲）
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x8b6f47,
      roughness: 0.9,
      metalness: 0.0
    });

    // 使用多个圆柱创建弯曲效果
    for (let i = 0; i < 5; i++) {
      const segment = new THREE.Mesh(
        new THREE.CylinderGeometry(0.25 - i * 0.03, 0.28 - i * 0.03, 1.5, 8),
        trunkMaterial
      );
      segment.position.set(
        i * 0.3,
        1 + i * 1.5,
        0
      );
      segment.rotation.z = i * 0.1;
      segment.castShadow = true;
      segment.receiveShadow = true;
      tree.add(segment);
    }

    // 棕榈叶（6-8片）
    const leafMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a5a1f,
      roughness: 0.8,
      metalness: 0.0,
      side: THREE.DoubleSide
    });

    const leafCount = 6 + Math.floor(Math.random() * 3);
    for (let i = 0; i < leafCount; i++) {
      const leaf = new THREE.Mesh(
        new THREE.PlaneGeometry(0.8, 4),
        leafMaterial
      );
      const angle = (i / leafCount) * Math.PI * 2;
      leaf.position.set(1.5, 8.5, 0);
      leaf.rotation.z = Math.PI / 3;
      leaf.rotation.y = angle;
      leaf.castShadow = true;
      tree.add(leaf);
    }

    return tree;
  }

  private addBuildings(group: THREE.Group, size: number): void {
    // 添加4-7个建筑
    const buildingCount = 4 + Math.floor(Math.random() * 4);

    for (let i = 0; i < buildingCount; i++) {
      const buildingType = Math.random();
      let building: THREE.Group;

      if (buildingType < 0.4) {
        building = this.createHouse();
      } else if (buildingType < 0.7) {
        building = this.createWarehouse();
      } else {
        building = this.createTower();
      }

      // 放置建筑（在外围）
      const angle = (i / buildingCount) * Math.PI * 2 + Math.random() * 0.3;
      const distance = size * 0.6 + Math.random() * size * 0.25;
      building.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );
      building.rotation.y = Math.random() * Math.PI * 2;

      group.add(building);
    }
  }

  private createHouse(): THREE.Group {
    const house = new THREE.Group();

    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4a574,
      roughness: 0.8,
      metalness: 0.1
    });

    const roofMaterial = new THREE.MeshStandardMaterial({
      color: 0x8b4513,
      roughness: 0.9,
      metalness: 0.0
    });

    // 主体
    const walls = new THREE.Mesh(
      new THREE.BoxGeometry(6, 4, 5),
      wallMaterial
    );
    walls.position.y = 2;
    walls.castShadow = true;
    walls.receiveShadow = true;
    house.add(walls);

    // 屋顶
    const roof = new THREE.Mesh(
      new THREE.ConeGeometry(4.5, 2.5, 4),
      roofMaterial
    );
    roof.position.y = 5.25;
    roof.rotation.y = Math.PI / 4;
    roof.castShadow = true;
    roof.receiveShadow = true;
    house.add(roof);

    // 门
    const door = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 2.5, 0.2),
      new THREE.MeshStandardMaterial({ color: 0x654321 })
    );
    door.position.set(0, 1.25, 2.6);
    door.castShadow = true;
    house.add(door);

    // 窗户
    const windowMaterial = new THREE.MeshStandardMaterial({
      color: 0x87ceeb,
      transparent: true,
      opacity: 0.6
    });

    const window1 = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 0.1),
      windowMaterial
    );
    window1.position.set(-1.5, 2.5, 2.55);
    house.add(window1);

    const window2 = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 0.1),
      windowMaterial
    );
    window2.position.set(1.5, 2.5, 2.55);
    house.add(window2);

    return house;
  }

  private createWarehouse(): THREE.Group {
    const warehouse = new THREE.Group();

    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0x7a7a7a,
      roughness: 0.7,
      metalness: 0.3
    });

    // 主体（更大更方正）
    const main = new THREE.Mesh(
      new THREE.BoxGeometry(8, 5, 10),
      wallMaterial
    );
    main.position.y = 2.5;
    main.castShadow = true;
    main.receiveShadow = true;
    warehouse.add(main);

    // 平屋顶
    const roof = new THREE.Mesh(
      new THREE.BoxGeometry(8.5, 0.5, 10.5),
      new THREE.MeshStandardMaterial({
        color: 0x555555,
        roughness: 0.6,
        metalness: 0.4
      })
    );
    roof.position.y = 5.25;
    roof.castShadow = true;
    warehouse.add(roof);

    // 大门
    const door = new THREE.Mesh(
      new THREE.BoxGeometry(4, 4, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x4a4a4a })
    );
    door.position.set(0, 2, 5.15);
    door.castShadow = true;
    warehouse.add(door);

    return warehouse;
  }

  private createTower(): THREE.Group {
    const tower = new THREE.Group();

    const stoneMaterial = new THREE.MeshStandardMaterial({
      color: 0x8b8680,
      roughness: 0.9,
      metalness: 0.1
    });

    // 塔身（多层）
    for (let i = 0; i < 4; i++) {
      const section = new THREE.Mesh(
        new THREE.CylinderGeometry(2 - i * 0.2, 2.2 - i * 0.2, 3, 8),
        stoneMaterial
      );
      section.position.y = 1.5 + i * 3;
      section.castShadow = true;
      section.receiveShadow = true;
      tower.add(section);
    }

    // 塔顶
    const top = new THREE.Mesh(
      new THREE.ConeGeometry(2, 2.5, 8),
      new THREE.MeshStandardMaterial({
        color: 0x6b6b6b,
        roughness: 0.7,
        metalness: 0.3
      })
    );
    top.position.y = 14;
    top.castShadow = true;
    tower.add(top);

    // 窗户
    const windowMaterial = new THREE.MeshStandardMaterial({
      color: 0xffff88,
      emissive: 0xffaa00,
      emissiveIntensity: 0.2
    });

    for (let i = 1; i < 4; i++) {
      const window = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 0.8, 0.1),
        windowMaterial
      );
      window.position.set(1.8 - i * 0.15, 1.5 + i * 3, 0);
      tower.add(window);
    }

    return tower;
  }

  private addRiver(group: THREE.Group, size: number): void {
    // 创建一条蜿蜒的河流
    const segments = 8;
    const riverWidth = 6;

    // 生成河流路径点
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const x = (t - 0.5) * size * 1.5;
      const z = Math.sin(t * Math.PI * 3) * size * 0.4;
      points.push(new THREE.Vector3(x, 0, z));
    }

    // 创建河流曲线
    const curve = new THREE.CatmullRomCurve3(points);
    const riverPoints = curve.getPoints(50);

    // 创建河流几何体
    const riverGroup = new THREE.Group();

    for (let i = 0; i < riverPoints.length - 1; i++) {
      const p1 = riverPoints[i];
      const p2 = riverPoints[i + 1];

      const direction = new THREE.Vector3().subVectors(p2, p1).normalize();
      const perpendicular = new THREE.Vector3(-direction.z, 0, direction.x);

      const vertices = new Float32Array([
        p1.x + perpendicular.x * riverWidth / 2, 0.02, p1.z + perpendicular.z * riverWidth / 2,
        p1.x - perpendicular.x * riverWidth / 2, 0.02, p1.z - perpendicular.z * riverWidth / 2,
        p2.x + perpendicular.x * riverWidth / 2, 0.02, p2.z + perpendicular.z * riverWidth / 2,
        p2.x - perpendicular.x * riverWidth / 2, 0.02, p2.z - perpendicular.z * riverWidth / 2
      ]);

      const indices = new Uint16Array([0, 1, 2, 1, 3, 2]);

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));
      geometry.computeVertexNormals();

      const waterMaterial = new THREE.MeshStandardMaterial({
        color: 0x1e90ff,
        transparent: true,
        opacity: 0.6,
        roughness: 0.1,
        metalness: 0.8
      });

      const segment = new THREE.Mesh(geometry, waterMaterial);
      segment.receiveShadow = true;
      riverGroup.add(segment);
    }

    group.add(riverGroup);
  }

  private addDecorations(group: THREE.Group, size: number): void {
    // 添加栅栏
    this.addFences(group, size);

    // 添加路标
    this.addSigns(group, size);

    // 添加木桶
    this.addBarrels(group, size);
  }

  private addFences(group: THREE.Group, size: number): void {
    const fenceCount = 5 + Math.floor(Math.random() * 5);

    for (let i = 0; i < fenceCount; i++) {
      const fence = new THREE.Group();
      const fenceLength = 8 + Math.random() * 6;
      const posts = Math.floor(fenceLength / 2) + 1;

      const woodMaterial = new THREE.MeshStandardMaterial({
        color: 0x8b7355,
        roughness: 0.9,
        metalness: 0.0
      });

      // 立柱
      for (let j = 0; j < posts; j++) {
        const post = new THREE.Mesh(
          new THREE.BoxGeometry(0.2, 1.5, 0.2),
          woodMaterial
        );
        post.position.set(j * 2, 0.75, 0);
        post.castShadow = true;
        fence.add(post);
      }

      // 横梁
      for (let j = 0; j < 2; j++) {
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(fenceLength, 0.1, 0.1),
          woodMaterial
        );
        rail.position.set(fenceLength / 2 - 1, 0.5 + j * 0.5, 0);
        rail.castShadow = true;
        fence.add(rail);
      }

      // 放置栅栏
      const angle = (i / fenceCount) * Math.PI * 2;
      const distance = size * 0.4 + Math.random() * size * 0.3;
      fence.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );
      fence.rotation.y = angle + Math.PI / 2 + (Math.random() - 0.5) * 0.5;

      group.add(fence);
    }
  }

  private addSigns(group: THREE.Group, size: number): void {
    const signCount = 4 + Math.floor(Math.random() * 4);

    for (let i = 0; i < signCount; i++) {
      const sign = new THREE.Group();

      // 立柱
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, 0.12, 2.5, 8),
        new THREE.MeshStandardMaterial({ color: 0x654321 })
      );
      post.position.y = 1.25;
      post.castShadow = true;
      sign.add(post);

      // 标牌
      const board = new THREE.Mesh(
        new THREE.BoxGeometry(2, 1, 0.1),
        new THREE.MeshStandardMaterial({ color: 0xf0e68c })
      );
      board.position.y = 2.5;
      board.castShadow = true;
      sign.add(board);

      // 放置路标
      const angle = (i / signCount) * Math.PI * 2;
      const distance = size * 0.35 + Math.random() * size * 0.25;
      sign.position.set(
        Math.cos(angle) * distance,
        0,
        Math.sin(angle) * distance
      );
      sign.rotation.y = Math.random() * Math.PI * 2;

      group.add(sign);
    }
  }

  private addBarrels(group: THREE.Group, size: number): void {
    const barrelCount = 10 + Math.floor(Math.random() * 10);

    for (let i = 0; i < barrelCount; i++) {
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.4, 0.4, 1, 12),
        new THREE.MeshStandardMaterial({
          color: 0x8b6914,
          roughness: 0.8,
          metalness: 0.2
        })
      );

      barrel.position.set(
        (Math.random() - 0.5) * size * 1.5,
        0.5,
        (Math.random() - 0.5) * size * 1.5
      );
      barrel.rotation.set(
        (Math.random() - 0.5) * 0.3,
        Math.random() * Math.PI * 2,
        (Math.random() - 0.5) * 0.3
      );
      barrel.castShadow = true;
      barrel.receiveShadow = true;

      group.add(barrel);
    }
  }

  createWalls(size: number = 50): THREE.Mesh[] {
    // 移除旧墙
    const oldWalls = this.scene.children.filter(child => child.name === 'wall');
    oldWalls.forEach(wall => this.scene.remove(wall));

    const wallHeight = 5;
    const wallThickness = 1;
    const arenaSize = size;

    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0x666666,
      roughness: 0.7
    });

    const wallMeshes: THREE.Mesh[] = [];

    // 四面墙
    const walls = [
      { x: 0, z: arenaSize, rx: 0, ry: 0, w: arenaSize * 2, h: wallHeight, d: wallThickness }, // 北
      { x: 0, z: -arenaSize, rx: 0, ry: 0, w: arenaSize * 2, h: wallHeight, d: wallThickness }, // 南
      { x: arenaSize, z: 0, rx: 0, ry: Math.PI / 2, w: arenaSize * 2, h: wallHeight, d: wallThickness }, // 东
      { x: -arenaSize, z: 0, rx: 0, ry: Math.PI / 2, w: arenaSize * 2, h: wallHeight, d: wallThickness } // 西
    ];

    walls.forEach(wall => {
      const geometry = new THREE.BoxGeometry(wall.w, wall.h, wall.d);
      const mesh = new THREE.Mesh(geometry, wallMaterial);
      mesh.name = 'wall';
      mesh.position.set(wall.x, wall.h / 2, wall.z);
      mesh.rotation.set(wall.rx, wall.ry, 0);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
      wallMeshes.push(mesh);
    });

    return wallMeshes;
  }

  clearObstacles(): void {
    const obstacles = this.scene.children.filter(child => (child as any).isObstacle);
    obstacles.forEach(obstacle => this.scene.remove(obstacle));
  }

  updateCamera(targetPosition: THREE.Vector3, targetRotation: number): void {
    // 第三人称跟随相机
    const cameraDistance = 35; // 增大相机距离以适应更大地图
    const cameraHeight = 20; // 增大相机高度获得更好视野

    const angle = targetRotation;
    const cameraX = targetPosition.x - Math.sin(angle) * cameraDistance;
    const cameraZ = targetPosition.z - Math.cos(angle) * cameraDistance;

    // 平滑相机移动
    this.camera.position.lerp(
      new THREE.Vector3(cameraX, targetPosition.y + cameraHeight, cameraZ),
      0.1
    );

    // 相机看向目标
    const lookAtPoint = new THREE.Vector3(
      targetPosition.x,
      targetPosition.y + 2,
      targetPosition.z
    );
    this.camera.lookAt(lookAtPoint);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  add(object: THREE.Object3D): void {
    this.scene.add(object);
  }

  remove(object: THREE.Object3D): void {
    this.scene.remove(object);
  }
}
